import { useContext, useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import {
  Input,
  TableRow,
  TableCell,
  Typography,
} from '@safedns/skydns-ui-react-lib';

import InputTags from '../inputTags/InputTags';
import TableRowButtons from './TableRowButtons';

import ModalContext from '../../contexts/modal-context';

import { updatedController } from '../../store/domainControllersReducer';
import {
  useGetUserQuery,
  useEditDomainControllerMutation,
  useGetDomainControllersQuery,
} from '../../services/ADapi';
import { notificationSuccess } from '../../store/notificationReducer';
import { validateIpAddress } from '../../utils/validatateIP';

import styles from '../inputTags/InputTags.module.scss';

/**
 *
 * @param {string | number} id
 * @param {name} name
 * @param {string[]} numberOfIPs
 * @param {boolean} isPDC
 * @returns
 */

const DomainControllerTableRow = ({ id, name, numberOfIPs, isPDC }) => {
  const dispatch = useDispatch();
  const [tags, setTags] = useState([]);
  const [isEditable, setEditableState] = useState();
  const [domainContollerName, setDomainContollerName] = useState(name);
  const [isSaveModeDisabled, setSaveModeState] = useState(false);
  const [responseErrors, setResponseErrors] = useState({});
  const { isShowRemoveModal, setIsShowRemoveModal } = useContext(ModalContext);

  const {
    data: { user_id },
  } = useGetUserQuery();

  const [editDomainController] = useEditDomainControllerMutation();
  const { data: domainControllerById } = useGetDomainControllersQuery(user_id, {
    selectFromResult: ({ data, isLoading }) => ({
      isLoading,
      data: data?.find((controller) => controller.id === id),
    }),
  });

  useEffect(() => {
    const areTagsAdded = tags.length > 0;
    const allTagsValid = tags.every((tag) => tag.className === '');

    setSaveModeState(allTagsValid && areTagsAdded);
  }, [tags]);

  useEffect(() => {
    const ipAddresses = domainControllerById.ips.map(({ ip }) => ({
      id: ip,
      text: ip,
      className: '',
    }));

    setTags(ipAddresses);
  }, [domainControllerById.ips]);

  const onToggleRemoveModal = () => {
    dispatch(
      updatedController({
        id: id,
        name: name,
      })
    );

    return setIsShowRemoveModal(!isShowRemoveModal);
  };

  const switchToEditMode = () => {
    setEditableState(true);
  };

  const handleAddition = (tag) => {
    const isValid = validateIpAddress(tag.text);

    if (!isValid) {
      tag.className = styles['ReactTags__tag_error'];
    }
    // If there are some spaces before and after a semicolon, then there will be incorrect behavior
    tag.text = tag.text.replaceAll(/\s*;\s*|;/g, '');
    tag.id = tag.id.replaceAll(/\s*;\s*|;/g, '');

    setTags((prevTags) => {
      return [...prevTags, tag];
    });
  };

  const handleDelete = (index) => {
    setResponseErrors((prevState) => {
      return {
        ...prevState,
        // When there are messages from regexp check, there is no such object in error state
        ips: prevState.ips?.filter((_, i) => i !== index),
      };
    });
    setTags(tags.filter((_, i) => i !== index));
  };

  const onTagUpdate = (index, newTag) => {
    const isValid = validateIpAddress(newTag.text);

    if (!isValid) {
      newTag.className = styles['ReactTags__tag_error'];
    }

    const updatedTags = [...tags];
    updatedTags.splice(index, 1, newTag);

    setTags(updatedTags);
  };

  const saveDomainController = async () => {
    const ips = tags.map((tag) => ({
      ip: tag.id,
    }));

    try {
      await editDomainController({
        user_id,
        domainController: { id, ips, name: domainContollerName },
      }).unwrap();

      setEditableState(false);
      setResponseErrors({});
      dispatch(
        notificationSuccess({
          isNotification: true,
          type: 'success',
          message: `Изменения сохранены`,
        })
      );
    } catch (error) {
      setEditableState(true);
      setResponseErrors(error);

      let errorIndexes = [];

      if (error.ips) {
        error.ips.forEach((item, index) => {
          if (item.ip) {
            errorIndexes.push(index);
          }
        });
      }

      const tagsCopy = [...tags];
      tagsCopy.forEach((tag, index) => {
        if (errorIndexes.includes(index)) {
          tag.className = styles['ReactTags__tag_error'];
        }
      });

      setTags(tagsCopy);
    }
  };

  const nameChangeHandler = (e) => {
    setDomainContollerName(e.target.value);
  };

  const declineEditMode = () => {
    setEditableState(false);
  };

  return (
    <TableRow>
      <TableCell sx={{ width: 300 }}>
        {isEditable ? (
          <>
            <Input
              value={domainContollerName}
              placeholder="Введите название домен контроллера"
              size="medium"
              onChange={nameChangeHandler}
              sx={{ width: '100%' }}
            />
            {responseErrors.name ? (
              <Typography variant="body2" color="#e08d8d">
                {responseErrors.name}
              </Typography>
            ) : null}
          </>
        ) : (
          name
        )}
      </TableCell>
      <TableCell sx={{ width: 350 }}>
        {isEditable ? (
          <>
            <InputTags
              tags={tags}
              handleAddition={handleAddition}
              handleDelete={handleDelete}
              onTagUpdate={onTagUpdate}
            />
            {responseErrors.ips
              ? responseErrors.ips.map((item, i) => (
                  <Typography
                    key={`${i}-${item.ip}`}
                    variant="body2"
                    color="#e08d8d"
                  >
                    {item.ip}
                  </Typography>
                ))
              : null}
          </>
        ) : (
          `${numberOfIPs} IP`
        )}
      </TableCell>
      <TableCell>{isPDC ? 'Основной' : 'Дополнительный'}</TableCell>
      <TableRowButtons
        isEditable={isEditable}
        isPDC={isPDC}
        isSaveDisabled={!isSaveModeDisabled}
        onSave={saveDomainController}
        onToggleRemoveModal={onToggleRemoveModal}
        switchToEditMode={switchToEditMode}
        declineEditMode={declineEditMode}
      />
    </TableRow>
  );
};

export default DomainControllerTableRow;
